import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * Success page displayed after successful Stripe checkout.
 * This page waits for the webhook to process the subscription
 * and then redirects the user to their dashboard.
 */
const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [countdown, setCountdown] = useState(10);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      toast.error('No session ID found in URL');
      return;
    }

    // Show success message immediately - webhook will handle the backend
    toast.success('Payment successful! Your subscription is being activated...');
    
    // Wait a moment for webhook processing, then redirect
    const timer = setTimeout(() => {
      setStatus('success');
      toast.success('Subscription activated! Redirecting to dashboard...');
      
      // Start countdown
      const countdownTimer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimer);
            navigate('/dashboard');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownTimer);
    }, 3000); // Give webhook 3 seconds to process

    return () => clearTimeout(timer);
  }, [sessionId, navigate]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '70vh',
        padding: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          textAlign: 'center',
          maxWidth: 500,
          width: '100%',
        }}
      >
        {status === 'processing' && (
          <>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h4" gutterBottom color="primary">
              Processing Your Subscription
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we activate your subscription...
            </Typography>
            {sessionId && (
              <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>
                Session ID: {sessionId}
              </Typography>
            )}
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleOutlineIcon 
              sx={{ fontSize: 80, color: 'success.main', mb: 2 }} 
            />
            <Typography variant="h4" gutterBottom color="success.main">
              Subscription Activated!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Welcome to ShieldShot! Your subscription has been successfully activated.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Redirecting to dashboard in {countdown} seconds...
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleGoToDashboard}
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Typography variant="h4" gutterBottom color="error">
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We couldn't process your subscription. Please contact support.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/subscriptions')}
            >
              Back to Subscriptions
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SubscriptionSuccess;
